import { useWizardStore } from '../../store/wizard.js';
import { Card } from '../common/Card.js';
import { Button } from '../common/Button.js';
import { Icon } from '../common/Icon.js';
import { pushCompletedId } from '../../lib/history.js';

const CONFIDENCE_LABEL: Record<'high' | 'medium' | 'low', string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

/**
 * Conclusion screen built from the server-provided `result` (scientific name,
 * common names, confidence, reasoning) plus the candidate list it considered.
 */
export function ResultScreen(): JSX.Element | null {
  const result = useWizardStore(s => s.result);
  const candidates = useWizardStore(s => s.candidates);
  const reset = useWizardStore(s => s.reset);

  if (!result) return null;

  const others = candidates.filter(c => c.speciesId !== result.speciesId).slice(0, 4);
  const onAcknowledge = () => {
    pushCompletedId(result.speciesId);
    reset();
  };

  return (
    <main className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
      <Card raised>
        <header className="flex items-center gap-2 text-sage-700">
          <Icon name="leaf" />
          <span className="font-body text-sm uppercase tracking-wide">This is most likely:</span>
        </header>
        <h1 className="mt-2 font-display text-4xl italic text-ink-900">{result.scientificName}</h1>
        {result.commonNames?.[0] ? (
          <p className="font-body text-bark-500">{result.commonNames.join(', ')}</p>
        ) : null}
        <p className="mt-3 font-mono text-xs text-bark-500">{CONFIDENCE_LABEL[result.confidence]}</p>
        {result.reasoning ? (
          <>
            <h2 className="mt-6 font-display text-xl text-sage-900">Why?</h2>
            <p className="mt-2 whitespace-pre-wrap font-body text-sm text-ink-900">{result.reasoning}</p>
          </>
        ) : null}
        {others.length > 0 ? (
          <>
            <h2 className="mt-6 font-display text-xl text-sage-900">Also considered</h2>
            <ul className="mt-2 space-y-1 font-body text-sm">
              {others.map(c => (
                <li key={c.speciesId}>
                  <span className="italic">{c.scientificName}</span>
                  {c.commonNames?.[0] ? <span className="text-bark-500"> — {c.commonNames[0]}</span> : null}
                  <span className="ml-1 font-mono text-xs text-bark-500">({Math.round(c.probability * 100)}%)</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <div className="mt-6 flex gap-2">
          <Button variant="primary" onClick={onAcknowledge}>That's it!</Button>
          <Button variant="secondary" onClick={reset}>Start over</Button>
        </div>
      </Card>
    </main>
  );
}
